import React, { useEffect, useState } from 'react'
import getToken from '../../addTokenInterceptor'
import './index.scss'
import { API_DOWNLOAD_GUIDE, API_GET_PROJECTNAME } from '../../urls'
import logo from '../../assets/Logo.svg'
import bg from '../../assets/background2.png'
import { DownloadOutlined } from '@ant-design/icons'
import { useMsal } from '@azure/msal-react'
import KDDLanding from '../../Screens/KDDLanding'
import { getHeaders } from '../../services/headerconstants'
import { logout } from '../../utils/authUtils'
import NotificationBanner from '../NotificationBanner'
import { Dropdown, MenuProps } from 'antd'
import { BlockBlobClient } from '@azure/storage-blob'
import logoutIcon from '../../assets/logoutIcon.svg'
import { TelemetryLogger } from '../../services/telemetryLogger'
import { runtimeConfig } from '../../runtimeConfig'

export interface IApiRequestDownloadAndPreviewLink {
  admin_offering_name: string
  template_type: string
}

export interface IApiResponseDownloadAndPreviewLink {
  template_type: string
  links: Array<{
    template_filename: string
    download_link: string
    preview_link: string
  }>
}

const UserAuthorization: React.FC = () => {
  const { instance } = useMsal()
  const { accessToken, idToken, projectId, user_email, user_name } = getToken()
  const [userEmail, setUserEmail] = useState('')
  const [projectNames, setProjectNames] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (projectId && user_email) {
      setUserEmail(user_email)
      const fetchProjectNames = async () => {
        try {
          const names = await getProjectNames()
          setProjectNames(names)
        } catch (err) {
          console.error('Found an error in fetching name, kindly try again later')
        }
      }
      fetchProjectNames()
    }
  }, [projectId, user_email])

  const getProjectNames = async () => {
    const data = {
      projectId: `${projectId}`,
    }
    const response = await fetch(API_GET_PROJECTNAME, {
      method: 'POST',
      headers: getHeaders(accessToken, idToken, projectId),
      body: JSON.stringify(data),
    })

    TelemetryLogger.instance.logRestApiCall(response, API_GET_PROJECTNAME, data);

    if (!response?.ok) {
      const message = `An error has occurred: ${response.statusText}`
      throw new Error(message)
    } else {
      const result = await response.json()
      return result.result.data
    }
  }

  const extractInitials = (fullName: string) => {
    const names = fullName.split(',')
    const firstName = names?.[1]?.trim()
    const lastName = names?.[0]?.trim()
    const firstNameInitial = firstName?.charAt(0)?.toUpperCase()
    const lastNameInitial = lastName?.charAt(0)?.toUpperCase()
    if (lastNameInitial && firstNameInitial) return `${lastNameInitial}${firstNameInitial}`
    if (lastNameInitial) return `${lastNameInitial}`
    if (firstNameInitial) return `${firstNameInitial}`
    return ''
  }

  const onClickLogoutHandler = () => {
    TelemetryLogger.instance.logUserAction("User attempting logout");

    logout(instance);
  };

  const items: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <div className='email-badge'>
          <div className='button-align'>{extractInitials(user_name)}</div>
          <div>
            <div className='username'>{user_name}</div>
            <div className='email-content'>{userEmail}</div>
          </div>
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <div className='sign-out' onClick={onClickLogoutHandler}>
          <img src={logoutIcon} alt="logout"/>
          Log Out
        </div>
      ),
    },
  ]

  const contentStyle: React.CSSProperties = {
    backgroundColor: '#2e3238',
    color: '#d1d3d6',
    borderRadius: '4px',
  }

  const menuStyle: React.CSSProperties = {
    backgroundColor: '#41464E',
    color: '#d1d3d6',
    padding: '12px',
  }

  const getDownloadAndPreviewLink = async (
    payload: IApiRequestDownloadAndPreviewLink,
  ): Promise<IApiResponseDownloadAndPreviewLink> => {
    const queryParams = new URLSearchParams({
      admin_offering_name: payload.admin_offering_name,
      template_type: payload.template_type,
    })

    const downloadService = new URL(runtimeConfig.VITE_USER_GUIDE_URL)
    console.log('downloadService', downloadService)
    const url = `${downloadService}${API_DOWNLOAD_GUIDE}?${queryParams.toString()}`

    try {
      const response = await fetch(url, { headers: getHeaders(accessToken, idToken, projectId) })

      TelemetryLogger.instance.logRestApiCall(response, url);

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const data = await response.json()
      return data // Assuming the response contains a 'links' array
    } catch (error) {
      console.error('Error fetching download and preview link:', error)
      throw error
    }
  }

  const downloadFiles = async (files: IApiResponseDownloadAndPreviewLink) => {
    for (const linkObject of files.links) {
      const downloadHref = linkObject.download_link
      const downloadName = linkObject.template_filename
      const blockBlobClient = new BlockBlobClient(downloadHref)

      try {
        const downloadResponse = await blockBlobClient.download()
        const blob = await downloadResponse.blobBody
        if (blob) {
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = downloadName
          a.click()
        }
      } catch (error) {
        console.error('Error downloading file:', error)
      }
    }
  }

  const fetchDataAndDownload = async () => {
    TelemetryLogger.instance.logUserAction("User clicks on download userguide");

    try {
      setLoading(true)
      const links = await getDownloadAndPreviewLink({
        admin_offering_name: 'Common-Userguide',
        template_type: 'userguide',
      })
      await downloadFiles(links)
    } catch (error) {
      console.error('Error in fetching and downloading files:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {projectNames == '' || projectNames == null ? (
        <div>
          <div style={{ width: '100%', zIndex: '999' }}>

            {/*NOTE: Header component starts*/}
            <div className='header'>
              <div className='header-left'>
                <img src={logo} alt='deloitte logo' className='header-logo' />
                <div className='vertical-line' />
                <span className='header-text'>
                  Gen AI<sup>TM</sup>
                </span>
              </div>
              <div className='header-right'>
                <div className='float-right'>
                  <div className='email-text'>
                    <a onClick={fetchDataAndDownload} className='content download-user-guide'>
                      <DownloadOutlined className='download-icon' />{' '}
                      {loading ? 'Downloading Guide...' : 'Download User Guide'}
                    </a>
                  </div>
                  <div className='menu-center'>
                    <Dropdown
                      menu={{ items }}
                      placement='bottomRight'
                      dropdownRender={(menu) => (
                        <div style={contentStyle}>
                          {React.cloneElement(
                            menu as React.ReactElement<{
                              style: React.CSSProperties
                            }>,
                            { style: menuStyle },
                          )}
                        </div>
                      )}
                    >
                      <div className='button-align'>{extractInitials(user_name)}</div>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
            {/*NOTE: Header component ends*/}

            <NotificationBanner />

          </div>
          <div className='parent'>
            <img className='image1' src={bg} />
          </div>
          {projectNames == '' ? (
            <h2>
              You seem to access wrong URL, please verify that you are accessing the correct URL.
            </h2>
          ) : (
            <h2>Please wait... We are checking your access authorization!!</h2>
          )}
        </div>
      ) : (
        <KDDLanding />
      )}
    </>
  )
}

export default UserAuthorization
